import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';

import { analytics } from 'core/analytics';
import { subscriptionStatusObservable } from 'core/client';
import { useContextManager } from 'apps/context/hooks';
import { useClientStore } from 'apps/hooks';

import { Dropdown, Tooltip, Avatar, Box, Icon } from 'components';
import { ArrowDown } from 'icons';

import { Editor } from 'libs/editor';
import { html } from 'libs/editor/serializers/html';
import { EditorStore } from 'libs/editor/store';
import PropTypes from 'prop-types';

import { ChatButtons } from './chat.styled';

const ChatInput = () => {
  const mediaMenuHolderRef = React.createRef(null);
  const toolbarHolderRef = React.createRef(null);

  const contextManager = useContextManager();
  const clientStore = useClientStore();
  const { signedInProfile } = clientStore;
  const isIntermediator = clientStore.hasRole('INTERMEDIATOR');

  const isFirstInput = useRef(true);

  // This types of slate nodes will be allowed to be sent as
  // first element in the messages
  const availableNodeTypesForSending = [
    'linkified-link',
    'preview-block',
    'preview',
    'video-block',
    'image-block',
  ];

  const store = useMemo(() => {
    return new EditorStore({
      formatting: 'chat',
      placeholder: 'Leave a message...',
      initialValue: html.deserialize('<p></p>'),
      readOnly: false,
      showMediaMenu: true,
      toolbarAlwaysVisible: true,
      mediaMenuAlwaysVisible: true,
      associatedSpaceId: () => {
        return contextManager.associatedSpaceId;
      },
    });
  }, []);

  const allowSend = () => {
    return (
      (availableNodeTypesForSending.includes(
        store.value.document.nodes.get(0)?.get('type'),
      ) ||
        store.value.document.text.length > 0) &&
      clientStore?.uploader?.queue.length === 0
    );
  };

  const canSend = useMemo(allowSend, [
    store.value.document,
    clientStore?.uploader?.queue,
  ]);

  useEffect(() => {
    store.mediaMenuStore.component = mediaMenuHolderRef?.current;
  }, [mediaMenuHolderRef]);
  useEffect(() => {
    store.toolbarStore.component = toolbarHolderRef?.current;
    store.toolbarStore.component = toolbarHolderRef?.current;
  }, [toolbarHolderRef]);
  useEffect(() => {
    if (store.reference) {
      store.reference.focus();
    }
  }, []);

  const handleSubmit = event => {
    event?.preventDefault();
    if (!allowSend()) return;

    // Force the store to compute the markdown value
    store.setMarkdownValue();

    if (!contextManager.chatAvailable) return;

    if (store.markdownValue.length) {
      contextManager.SendMessage(store.markdownValue);
      store.ResetValue();
      analytics.sendEvent('viewer-chat-send-message');
      // Took it from here
      // https://github.com/ianstormtaylor/slate/issues/2097#issuecomment-465236489
      setTimeout(() => {
        if (store.reference) {
          store.reference.focus();
        }
      }, 1);
    }
  };

  const handleMobileButtonClick = event => {
    event?.preventDefault();
    // When textArea is a reference
    const { reference } = store;

    if (!allowSend()) return;

    if (!contextManager.chatAvailable) return;

    if (reference && reference.value.length) {
      contextManager.SendMessage(reference.value);
      analytics.sendEvent('viewer-chat-send-message');
      reference.value = '';
    }
  };

  const handleInputFocus = () => {
    if (isFirstInput.current) {
      isFirstInput.current = false;
      return;
    }
    contextManager.setReadMessage(contextManager.lastMessage?.id);
    contextManager.unreadCount = 0;
  };

  const handleItemSelect = user => {
    if (signedInProfile.username === user.value) {
      contextManager.SetSendAsId(null);
    } else {
      contextManager.SetSendAsId(user.id);
    }
  };

  let users = [];
  // TODO: alex filter by role (intermediator)
  if (contextManager.participants.length > 0) {
    users = contextManager.participants.map(item => ({
      id: item.user.id,
      text: item.user.username,
      value: item.user.username,
      icon: (
        <Avatar
          src={item.user.profile.avatar?.url}
          name={item.user.profile.fullName}
        />
      ),
    }));
  }

  return (
    <>
      <ToolbarHolder id='toolbar-holder' ref={toolbarHolderRef}>
        {isIntermediator ? (
          <Box sx={{ position: 'absolute', right: 0 }}>
            <Dropdown
              autoWidth
              size='small'
              placement='top'
              iconRight={<ArrowDown />}
              selectedItemIndex={null}
              items={users}
              trigger={<></>}
              variant='outlined'
              placeholder='Select a username'
              onItemSelected={handleItemSelect}
              selectedItemPrefix='Send as:'
            />
          </Box>
        ) : null}
      </ToolbarHolder>
      <ChatGroupStyled>
        <Tooltip delayShow={500} place='top' id='inputTooltip' />

        <Editor
          store={store}
          placeholder='Your message here'
          onFocus={handleInputFocus}
          onEnterKeyDown={handleSubmit}
          style={{ maxHeight: '50vh', overflowY: 'auto' }}
          previewMode='chat'
          previewDownloadButtonHidden={true}
        />

        <ChatButtons
          disabled={
            !canSend ||
            !contextManager.chatAvailable ||
            subscriptionStatusObservable.status === 'disconnected'
          }
          // Check Slate Editor API
          active={allowSend()}
          onClick={
            clientStore.isMobile ? handleMobileButtonClick : handleSubmit
          }
        >
          <Icon name='send' viewBox='0 0 40 40' size='xmedium' />
        </ChatButtons>
      </ChatGroupStyled>
      <MediaMenuHolder
        id='mediamenu-holder'
        ref={mediaMenuHolderRef}
      ></MediaMenuHolder>
    </>
  );
};

export default observer(ChatInput);

ChatInput.propTypes = {
  onInputFocus: PropTypes.func,
  onScrollBottom: PropTypes.func,
};

export const ChatGroupStyled = styled.div(({ theme }) => ({
  padding: `${theme.space[2]} ${theme.space[3]}`,
  background: theme.color.wfWhite,
  maxHeight: '50vh',
  width: '100%',
}));

export const MediaMenuHolder = styled.div``;
export const ToolbarHolder = styled.div``;
